import React from 'react';
import { styled } from 'linaria/react';
import PaginationContext from '@jetshop/core/components/Pagination/PaginationContext';
import formatMessage, { useIntl } from '@jetshop/intl';
import qs from 'qs';
import { useLocation } from 'react-router';
import { theme } from '../../Theming/Theming';

//SHARED LOOK FOR PREV, NEXT AND THE PAGE SELECT. ONLY FONT-COLOR ON WHITE, SO IT READS ON EVERY THEME
const box = `
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 20px;
  border: 1px solid ${theme.colors.font};
  border-radius: ${theme.general.radius};
  background: ${theme.colors.white};
  color: ${theme.colors.font};
  font-family: ${theme.font.primary};
  font-weight: ${theme.weights.normal};
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition: background 150ms;
  &:hover,
  &:focus-visible {
    color: ${theme.colors.font};
    background: ${theme.colors.lightgrey};
  }
`;

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 2rem 0;
`;

const Controls = styled('div')`
  display: flex;
  justify-content: center;
  max-width: 100%;
  > * + * {
    margin-left: 8px;
  }
`;

const PrevButton = styled('a')`
  ${box};
  min-width: 120px;
  svg {
    margin-right: 8px;
  }
  &[data-disabled='true'] {
    opacity: 0.35;
    cursor: default;
    background: ${theme.colors.white};
  }
  ${theme.below.sm} {
    min-width: 0;
    padding: 0 16px;
    svg {
      margin: 0;
    }
    .label {
      display: none;
    }
  }
`;
const NextButton = styled(PrevButton)`
  svg {
    margin: 0 0 0 8px;
  }
`;

const SelectWrapper = styled('div')`
  position: relative;
`;

const SelectButton = styled('button')`
  ${box};
  min-width: 150px;
  svg {
    margin-left: 8px;
    transition: transform 200ms;
  }
  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
  ${theme.below.sm} {
    min-width: 0;
  }
`;

//ALL PAGE LINKS STAY IN THE DOM WHEN CLOSED, SO CRAWLERS STILL FIND EVERY PAGE
const PageList = styled('div')`
  display: none;
  position: absolute;
  z-index: 50;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 232px;
  overflow-y: auto;
  padding: 4px 0;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.general.radius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  &[data-open='true'] {
    display: block;
  }
`;

const Page = styled('a')`
  display: block;
  padding: 8px 12px;
  text-align: center;
  color: ${theme.colors.font};
  font-family: ${theme.font.primary};
  font-weight: ${theme.weights.normal};
  font-size: 14px;
  text-decoration: none;
  &:hover,
  &:focus-visible {
    color: ${theme.colors.font};
    background: ${theme.colors.lightgrey};
  }
  &[data-selected='true'] {
    font-weight: ${theme.weights.bold};
    background: ${theme.colors.lightgrey};
  }
`;

const Progress = styled('div')`
  position: relative;
  width: 170px;
  max-width: 100%;
  height: 2px;
  margin-top: 1.5rem;
  background: ${theme.colors.border};
  overflow: hidden;
  span {
    display: block;
    height: 100%;
    background: ${theme.colors.font};
    transition: width 300ms;
  }
`;

const Chevron = ({ direction }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    aria-hidden="true"
    style={{
      transform: { left: 'rotate(90deg)', right: 'rotate(-90deg)' }[direction]
    }}
  >
    <path
      d="M1 3l4 4 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function getPageUrl(location, queryParam, page) {
  const currentLocation = qs.parse(
    typeof window === 'undefined' ? location.search : window.location.search,
    {
      ignoreQueryPrefix: true
    }
  );
  const url =
    location.pathname +
    qs.stringify(
      Object.assign(Object.assign({}, currentLocation), {
        [queryParam]: page
      }),
      { addQueryPrefix: true, encodeValuesOnly: true, skipNulls: true }
    );
  return url;
}

const PageControl = ({
  totalPages,
  currentPage,
  goToPage,
  location,
  queryParam
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef();
  const listRef = React.useRef();

  React.useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const selected = list.querySelector('[data-selected="true"]');
    if (selected) {
      list.scrollTop =
        selected.offsetTop - list.clientHeight / 2 + selected.clientHeight / 2;
    }
    const closeOutside = e => {
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const closeOnEscape = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('touchstart', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('touchstart', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  //CREATE ARRAY WITH PAGE NUMBERS (1 TO TOTALPAGES)
  const indexArray = Array.from(Array(totalPages), (e, i) => i + 1);
  return (
    <SelectWrapper ref={wrapperRef}>
      <SelectButton
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {formatMessage('Page { currentPage } of { totalPages }', {
          currentPage,
          totalPages
        })}
        <Chevron />
      </SelectButton>
      <PageList ref={listRef} data-open={open}>
        {indexArray.map(page => {
          return (
            <Page
              key={page}
              href={getPageUrl(location, queryParam, page)}
              onClick={e => {
                e.preventDefault();
                setOpen(false);
                goToPage(page);
              }}
              data-selected={currentPage === page ? true : null}
              aria-current={currentPage === page ? 'page' : null}
            >
              {formatMessage('Page { page }', { page })}
            </Page>
          );
        })}
      </PageList>
    </SelectWrapper>
  );
};

export const PaginationWrapper = ({
  prevPage,
  nextPage,
  currentPage,
  totalPages,
  isFirstPage,
  isLastPage,
  hasPages,
  goToPage,
  className = ''
}) => {
  const location = useLocation();
  const { queryParam } = React.useContext(PaginationContext);
  //ARIA-LABEL NEEDS A PLAIN STRING, formatMessage RETURNS AN ELEMENT
  const t = useIntl();

  //NOTHING TO PAGINATE WITH A SINGLE PAGE, SO HIDE IT
  return hasPages && totalPages > 1 ? (
    <Wrapper className={className}>
      <Controls>
        <PrevButton
          href={getPageUrl(
            location,
            queryParam,
            isFirstPage ? currentPage : currentPage - 1
          )}
          onClick={e => {
            e.preventDefault();
            prevPage();
          }}
          data-disabled={isFirstPage ? true : null}
          aria-label={t('Prev')}
        >
          <Chevron direction="left" />
          <span className="label">{formatMessage('Prev')}</span>
        </PrevButton>
        <PageControl
          totalPages={totalPages}
          currentPage={currentPage}
          goToPage={goToPage}
          location={location}
          queryParam={queryParam}
        />
        <NextButton
          href={getPageUrl(
            location,
            queryParam,
            isLastPage ? currentPage : currentPage + 1
          )}
          onClick={e => {
            e.preventDefault();
            nextPage();
          }}
          data-disabled={isLastPage ? true : null}
          aria-label={t('Next')}
        >
          <span className="label">{formatMessage('Next')}</span>
          <Chevron direction="right" />
        </NextButton>
      </Controls>
      <Progress aria-hidden="true">
        <span style={{ width: (currentPage / totalPages) * 100 + '%' }} />
      </Progress>
    </Wrapper>
  ) : null;
};
